import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Header from './components/Header';

// Pages
import Landing from './pages/Landing';
import Features from './pages/Features';
import Contact from './pages/Contact';
import Login from './pages/Login';
import AdminSignUp from './pages/AdminSignUp';
import DevLogin from './pages/DevLogin';
import Dashboard from './pages/Dashboard';
import ChatPage from './pages/ChatPage';
import AdminDashboard from './pages/AdminDashboard';
import TestCreation from './pages/admin/TestCreation';
import TestEditor from './pages/admin/TestEditor';
import TestResults from './pages/admin/TestResults';
import DocumentUpload from './pages/admin/DocumentUpload';
import EmployeeDashboard from './pages/EmployeeDashboard';
import PendingTests from './pages/employee/PendingTests';
import TakeTest from './pages/employee/TakeTest';
import TakeAssignedTest from './pages/employee/TakeAssignedTest';
import Unauthorized from './pages/Unauthorized';
import OrganizationSetup from './pages/OrganizationSetup';
import WaitingForInvite from './pages/WaitingForInvite';

// Get API URL from environment or use default
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function App() {
  const [collections, setCollections] = useState<string[]>([]);

  useEffect(() => {
    fetchCollections();
  }, []);

  const fetchCollections = async () => {
    try {
      const response = await fetch(`${API_URL}/collections`);
      const data = await response.json();
      setCollections(data.collections || []);
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Header collectionsCount={collections.length} />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/features" element={<Features />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin/signup" element={<AdminSignUp />} />
            <Route path="/dev/login" element={<DevLogin />} />
            <Route
              path="/dashboard"
              element={
                <main className="App-main">
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="admin">
                    <AdminDashboard />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/admin/tests/create"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="admin">
                    <TestCreation />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/admin/tests/:testId/edit"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="admin">
                    <TestEditor />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/admin/tests/results"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="admin">
                    <TestResults />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/admin/documents/upload"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="admin">
                    <DocumentUpload />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/employee/dashboard"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="employee">
                    <EmployeeDashboard />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/employee/tests/pending"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="employee">
                    <PendingTests />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/employee/tests/:testId/take"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="employee">
                    <TakeTest />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route
              path="/assignments/:assignmentId/take"
              element={
                <main className="App-main">
                  <ProtectedRoute requiredRole="employee">
                    <TakeAssignedTest />
                  </ProtectedRoute>
                </main>
              }
            />
            <Route path="/unauthorized" element={<Unauthorized />} />
            <Route
              path="/waiting-for-invite"
              element={
                <ProtectedRoute>
                  <WaitingForInvite />
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization/setup"
              element={
                <ProtectedRoute>
                  <OrganizationSetup />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;